import { useAtomValue } from "jotai/index";
import type { FunctionComponentWithRef } from "react";
import { graphql, useFragment } from "react-relay";

import { accountAtom } from "shared/lib/account";

import { withHighlightedHeadline } from "scmp-app/components/content/content-item-render/highlight/hoc";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import type { commentContentItemContent$key } from "scmp-app/queries/__generated__/commentContentItemContent.graphql";

import {
  ActionBar,
  AuthorImages,
  AuthorLinks,
  Container,
  Content,
  ContentContainer,
  Duration,
  Headline,
  StyledEntityLink,
  StyledVideoEntityLink,
  StyledVideoImage,
  StyledVideoImageContainer,
  VideoCommentContainer,
  VideoContentContainer,
  VideoHeadline,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: commentContentItemContent$key;
};

const Component: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  onClick,
  ref,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment commentContentItemContent on Content {
        entityId
        urlAlias
        ...entityLink
        ...hooksContentItemProvidersContent
          @arguments(
            withAuthorLink: true
            withHeadline: true
            withAuthorImage: true
            withActionBar: true
          )
        ... on Video {
          headline
          duration
          image(filter: { type: MAIN_IMAGE }) {
            alt
            style(filter: { style: "1200x800" }) {
              url
            }
          }
        }
      }
    `,
    reference,
  );

  const user = useAtomValue(accountAtom);
  const providers = useContentItemProviders(content);

  return content.image ? (
    <VideoCommentContainer className={className} ref={ref}>
      <VideoContentContainer>
        <StyledVideoEntityLink
          onClick={() => onClick?.(content.entityId, content.urlAlias)}
          reference={content}
        >
          <VideoHeadline>{content.headline}</VideoHeadline>
          <StyledVideoImageContainer>
            {content.image.style?.url && (
              <StyledVideoImage alt={content.image?.alt ?? ""} src={content.image.style.url} />
            )}
            {providers.video?.videoPlayIcon()}
            <Duration>{content.duration}</Duration>
          </StyledVideoImageContainer>
        </StyledVideoEntityLink>
      </VideoContentContainer>
    </VideoCommentContainer>
  ) : (
    <Container className={className} ref={ref}>
      <ContentContainer>
        <Content>
          <AuthorLinks onClick={() => onClick?.(content.entityId, content.urlAlias)}>
            {providers.author?.({ ignoreAuthorType: true, isMultiple: true })}
          </AuthorLinks>
          <StyledEntityLink
            onClick={() => onClick?.(content.entityId, content.urlAlias)}
            reference={content}
          >
            <Headline>
              {providers.headline({ hideTooltip: true, preferSocialHeadline: true })}
            </Headline>
          </StyledEntityLink>
        </Content>
        <AuthorImages>
          {providers.authorImage?.({
            ignoreAuthorType: true,
            isMultiple: true,
          })}
        </AuthorImages>
      </ContentContainer>
      <ActionBar>
        {providers.actionBar({
          commentIconPosition: "left",
          isBookmarkVisible: user.isLoggedIn,
          isCommentsVisible: true,
          isIconVisible: false,
        })}
      </ActionBar>
    </Container>
  );
};

Component.displayName = "ContentItemHomeComment";

export const ContentItemComment = withHighlightedHeadline(Component);
